import { ReactElement, useMemo, useCallback } from 'react';
import { DialogTitle } from '@mui/material';
import { TabContext } from '@mui/lab';
import { useSnackbar } from 'notistack';
import { useFormik, FormikProvider, FormikHelpers } from 'formik';
import { includes } from 'lodash';

import { TabPanelData } from '~globals/types/commons';
import { useLazyRequest } from '~hooks/useLazyRequest';
import { updateAdminIntegrationStockLocations } from '~services/admin/integration/stockLocation';

import { DialogIntegrationAssignWarehousesPanelId } from './types';
import { useDialogIntegrationAssignWarehousesContext } from './Context';
import {
  DialogIntegrationAssignWarehousesEmptyState,
  DialogIntegrationAssignWarehousesSelection,
} from './Tabs';
import { FIELDS_NAME, getInitialValues, validationSchema } from './utils';
import {
  DialogIntegrationAssignWarehousesContentContainer,
  DialogIntegrationAssignWarehousesTabPanel,
  DialogIntegrationAssignWarehousesActions,
  DialogIntegrationAssignWarehousesActionsButton,
  DialogIntegrationAssignWarehousesActionsButtonLoading,
} from './styles';

const TABS_PANELS: TabPanelData<DialogIntegrationAssignWarehousesPanelId>[] = [
  {
    id: 'emptyState',
    panel: DialogIntegrationAssignWarehousesEmptyState,
  },
  {
    id: 'selection',
    panel: DialogIntegrationAssignWarehousesSelection,
  },
];

const DialogIntegrationAssignWarehousesContent = (): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();

  const {
    companyIntegrationId,
    activeTab,
    stockLocations,
    onClose,
    onConfirmSuccess,
  } = useDialogIntegrationAssignWarehousesContext();

  const validateTabs = useCallback(
    (valids: DialogIntegrationAssignWarehousesPanelId[]) =>
      includes(valids, activeTab),
    [activeTab],
  );

  const [, loadingUpdateStockLocations, , executeUpdateStockLocations] =
    useLazyRequest({
      request: updateAdminIntegrationStockLocations,
      withPostSuccess: () => {
        const message = `La asignación de depósitos de origen fue realizada correctamente.`;

        enqueueSnackbar(message, { variant: 'success' });

        onConfirmSuccess?.();

        onClose();
      },
      withPostFailure: () => {
        enqueueSnackbar(
          'No se pudo realizar la asignación de depósitos de origen, intenté nuevamente',
          { variant: 'error' },
        );
      },
    });

  const onSubmit = useCallback(
    async (
      values: ReturnType<typeof getInitialValues>,
      { setSubmitting }: FormikHelpers<ReturnType<typeof getInitialValues>>,
    ) => {
      const networkNodeIds = values[FIELDS_NAME.NETWORK_NODE_IDS].join(',');

      await executeUpdateStockLocations({
        companyIntegrationId,
        networkNodeIds,
      });

      setSubmitting(false);
    },
    [companyIntegrationId, executeUpdateStockLocations],
  );

  const formikBag = useFormik({
    initialValues: getInitialValues(stockLocations),
    validationSchema,
    onSubmit,
    validateOnMount: true,
  });

  const { submitForm, isSubmitting, dirty, isValid } = formikBag;

  const loadingSubmit = useMemo(
    () => loadingUpdateStockLocations || isSubmitting,
    [loadingUpdateStockLocations, isSubmitting],
  );

  const disabledSubmit = useMemo(() => !dirty || !isValid, [dirty, isValid]);

  return (
    <FormikProvider value={formikBag}>
      <DialogTitle>Asignar depósitos de origen</DialogTitle>

      <DialogIntegrationAssignWarehousesContentContainer dividers>
        <TabContext value={activeTab}>
          {TABS_PANELS.map(({ id, panel: Panel }) => (
            <DialogIntegrationAssignWarehousesTabPanel
              key={`dialog-integration-assign-warehouses-panel-${id}`}
              value={id}
              sx={{ margin: validateTabs(['emptyState']) ? 'auto' : 0 }}
            >
              <Panel />
            </DialogIntegrationAssignWarehousesTabPanel>
          ))}
        </TabContext>
      </DialogIntegrationAssignWarehousesContentContainer>

      <DialogIntegrationAssignWarehousesActions>
        <DialogIntegrationAssignWarehousesActionsButton
          color="secondary"
          onClick={onClose}
        >
          Cerrar
        </DialogIntegrationAssignWarehousesActionsButton>

        {validateTabs(['selection']) && (
          <DialogIntegrationAssignWarehousesActionsButtonLoading
            variant="contained"
            color="primary"
            onClick={submitForm}
            loading={loadingSubmit}
            disabled={disabledSubmit}
          >
            Confirmar
          </DialogIntegrationAssignWarehousesActionsButtonLoading>
        )}
      </DialogIntegrationAssignWarehousesActions>
    </FormikProvider>
  );
};

export default DialogIntegrationAssignWarehousesContent;
